(ns ui.sticker-dialogs.emoji-slider
  "The Replicant emoji slider sticker dialog: a question, the slider
   emoji and a color, with a preview in the Instagram look."
  (:require [clojure.string :as str]
            [storrito.editor.emoji-slider-sticker :as sticker]
            [storrito.editor.sticker-design :as sticker-design]
            [storrito.editor.sticker-util :as sticker-util]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.emoji-picker :as emoji-picker]
            [ui.sticker-dialogs.model :as model]
            [ui.sticker-dialogs.ui :as ui]))

(def question-id
  "sticker-emoji-slider-question")

(def preview-width
  350)

(defn px
  "A length of the sticker on the canvas (see
   `storrito.editor.emoji-slider-sticker`) scaled to the preview."
  [size]
  (str (* (/ preview-width sticker/panel-width) size) "px"))

(defn initial-state
  [{:keys [data]}]
  {:question (or (:question data) "")
   :background-color (or (:background-color data) "#ffffff")
   :text-color (or (:text-color data) "#000000")
   :emoji (or (:emoji data) "😍")})

(defn submit-data
  [dialog]
  [:emoji-slider-sticker "Emoji Sticker"
   (select-keys dialog [:question :background-color :text-color :emoji])])

(defn preview
  "The sticker as on the canvas, scaled to `preview-width`: same
   geometry, same font and the same wrapping (at spaces, inside a word
   only when the word alone is too wide), so that the question breaks
   where it breaks on the canvas."
  [{:keys [question background-color text-color emoji]}]
  (let [slider-color (if (= background-color "#ffffff")
                       "#b604b0"
                       text-color)
        question? (not (str/blank? question))
        side-margin (/ (- sticker/panel-width sticker/question-width) 2)
        ;; The slider row is as high as its emoji. On the canvas the
        ;; slider sits in the middle of the `min-panel-height` area
        ;; below the question, so half of that height minus half of
        ;; the row is left below the row:
        row-margin (- (/ sticker/min-panel-height 2)
                      (/ sticker/slider-emoji-size 2))
        ;; ... and the gap plus the same half height minus the question
        ;; top and half of the row between the question and the row:
        question-bottom-margin (- (+ sticker/question-slider-gap
                                     (/ sticker/min-panel-height 2))
                                  sticker/question-top
                                  (/ sticker/slider-emoji-size 2))]
    [:div.flex.flex-col.items-center
     {:style {:box-shadow "0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12)"
              :width (px sticker/panel-width)
              :min-height (px sticker/min-panel-height)
              :border-radius (px sticker/panel-radius)
              :background-color background-color}}
     (when question?
       [:div.text-center
        {:style {:user-select "none"
                 :max-height (px (* sticker/question-max-lines
                                    sticker/question-line-height
                                    sticker/question-font-size))
                 :overflow "hidden"
                 :white-space "pre-wrap"
                 :font-size (px sticker/question-font-size)
                 :line-height (str sticker/question-line-height)
                 :font-family "Roboto"
                 :word-break "break-word"
                 :color text-color
                 :margin (str/join " " [(px sticker/question-top)
                                        (px side-margin)
                                        (px question-bottom-margin)
                                        (px side-margin)])}}
        question])
     [:div.relative.flex.justify-center.items-center
      {:style {:user-select "none"
               :height (px sticker/slider-emoji-size)
               :margin-top (px (if question? 0 row-margin))
               :margin-bottom (px row-margin)}}
      [:div {:style {:width (px sticker/slider-half-width)
                     :height (px sticker/slider-height)
                     :background-color slider-color
                     :border-radius (str/join " " [(px sticker/slider-radius) "0px" "0px" (px sticker/slider-radius)])}}]
      [:span.absolute
       {:style {:width (px sticker/slider-emoji-size)
                :height (px sticker/slider-emoji-size)
                :font-size (px sticker/slider-emoji-size)
                :line-height "1"
                :display "flex"
                :align-items "center"
                :justify-content "center"}}
       emoji]
      [:div {:style {:width (px sticker/slider-half-width)
                     :height (px sticker/slider-height)
                     :background-color (sticker-util/shadow-color background-color)
                     :border-radius (str/join " " ["0px" (px sticker/slider-radius) (px sticker/slider-radius) "0px"])}}]]]))

(defn panel
  [dialog]
  [:div.flex.flex-col.items-center
   [:form.m-0.flex.flex-col.items-center.gap-y-3.max-w-full
    {:style {:width (px sticker/panel-width)}
     :on {:submit [[:js/prevent-default]
                   [:sticker-dialog/submit]]}}
    [:div.relative.w-full
     (ui/text-field dialog
                    {:tag :textarea
                     :field :question
                     :dom-id question-id
                     :rows 3
                     :placeholder "Ask a question ..."
                     :autofocus? true
                     :class ["block" "border" "border-gray-400" "rounded" "p-2" "pr-10" "w-full" "resize-none"]})
     (ui/emoji-button {:field :question
                       :dom-id question-id
                       :class ui/emoji-button-inside-css})
     (emoji-picker/panel dialog :question)]
    [:div.relative.flex.items-center.gap-2
     [:span.text-sm.text-gray-700 "Slider emoji:"]
     [:button.text-3xl.px-2.py-1.rounded.border.border-gray-300.hover:bg-gray-100
      {:type "button"
       :title "Choose the slider emoji"
       :on {:click [[:js/prevent-default]
                    [:sticker-dialog/emoji-open :emoji nil :replace]]}}
      (:emoji dialog)]
     (emoji-picker/panel dialog :emoji)]
    (preview dialog)
    (ui/color-palette {:colors (vals sticker-design/question-colors)
                       :value (:background-color dialog)
                       :action-fn (fn [color]
                                    [[:sticker-dialog/assoc
                                      :background-color color
                                      :text-color (model/text-color-for color)]])})
    [:div.w-full.justify-center.flex
     (ui/primary-button {:type "submit"}
                        "Add Slider")]]])

(defn render
  [state]
  (when-let [dialog (actions/dialog state)]
    (panel dialog)))

(def modal
  {:modal-id :modals/editor2-emoji-slider-modal
   :on-load (fn [_state] [[:sticker-dialog/init :emoji-slider]])
   :sticker/kind :emoji-slider
   :sticker/single-asset-type :emoji-slider-sticker
   :sticker/initial-state #'initial-state
   :sticker/submit-data #'submit-data
   :render #'render})

(def register
  [modal])
